import React, { useState } from 'react';
import { useSettings } from './context';
import { S } from '../../styles';

// ── Settings › Shop › Closures and special hours (3 Nov) ─────────────────────
//
// One list for every day the shop is closed or keeps different hours (Manny,
// 3 Nov: "1."). It reaches online booking, fittings and rentals (a closed day
// is never offered), the after-hours text reply, and the website (a dated
// notice bar and the special hours). server/helpers/closures.js has the rules;
// sites/kit/closures.js writes the website's wording, mirrored in preview()
// below so the shop sees what visitors will.

const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const iso = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const addDays = (d, n) => { const t = new Date(`${d}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + n); return t.toISOString().slice(0, 10); };
const todayLocal = () => { const t = new Date(); return iso(t.getFullYear(), t.getMonth() + 1, t.getDate()); };
const longDate = (d, today) => {
  const [y, m, day] = d.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, day)).getUTCDay();
  return `${WEEKDAY[wd]}, ${MONTH[m - 1]} ${day}${Number(today.slice(0, 4)) !== y ? `, ${y}` : ''}`;
};

// The nth weekday of a month (n = -1 for the last), as YYYY-MM-DD.
function nthWeekday(y, m, wd, n) {
  if (n > 0) {
    const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    return iso(y, m, 1 + ((wd - first + 7) % 7) + (n - 1) * 7);
  }
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const last = new Date(Date.UTC(y, m - 1, lastDay)).getUTCDay();
  return iso(y, m, lastDay - ((last - wd + 7) % 7));
}
// Easter Sunday (the Anonymous Gregorian algorithm).
function easter(y) {
  const a = y % 19; const b = Math.floor(y / 100); const c = y % 100; const d = Math.floor(b / 4); const e = b % 4;
  const f = Math.floor((b + 8) / 25); const g = Math.floor((b - f + 1) / 3); const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4); const k = c % 4; const l = (32 + 2 * e + 2 * i - h - k) % 7; const m = Math.floor((a + 11 * h + 22 * l) / 451);
  return iso(y, Math.floor((h + l - 7 * m + 114) / 31), ((h + l - 7 * m + 114) % 31) + 1);
}
const HOLIDAYS = [
  ['Thanksgiving', (y) => nthWeekday(y, 11, 4, 4)],
  ['Christmas Eve', (y) => iso(y, 12, 24)],
  ['Christmas Day', (y) => iso(y, 12, 25)],
  ["New Year's Eve", (y) => iso(y, 12, 31)],
  ["New Year's Day", (y) => iso(y, 1, 1)],
  ['Easter Sunday', easter],
  ['Memorial Day', (y) => nthWeekday(y, 5, 1, -1)],
  ['Independence Day', (y) => iso(y, 7, 4)],
  ['Labor Day', (y) => nthWeekday(y, 9, 1, 1)],
];
// Each holiday's next date from today, soonest first.
export function upcomingHolidays(today = todayLocal()) {
  const y = Number(today.slice(0, 4));
  return HOLIDAYS.map(([name, f]) => ({ name, date: f(y) >= today ? f(y) : f(y + 1) })).sort((a, b) => a.date.localeCompare(b.date));
}

// What the website's bar will say (sites/kit/closures.js closureText).
export function preview(c, today = todayLocal()) {
  if (c.message) return c.message;
  const to = c.to && c.to >= c.from ? c.to : c.from;
  const lead = c.label ? `${c.label}: ` : '';
  let text;
  if (c.kind === 'hours') text = `${lead}open ${c.open && c.close ? `${time12(c.open)} – ${time12(c.close)}` : (c.hours || '…')} on ${c.from === to ? longDate(c.from, today) : `${longDate(c.from, today)} to ${longDate(to, today)}`}.`;
  else if (c.from === to) text = `${lead}${today === c.from ? 'closed today' : `closed ${longDate(c.from, today)}`}.`;
  else if (today >= c.from) text = `${lead}closed until ${longDate(to, today)}. Back ${longDate(addDays(to, 1), today)}.`;
  else text = `${lead}closed ${longDate(c.from, today)} to ${longDate(to, today)}.`;
  return text[0].toUpperCase() + text.slice(1);
}

const time12 = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 || 12}${m[2] === '00' ? '' : `:${m[2]}`}${h >= 12 ? 'pm' : 'am'}`;
};

const newId = () => `c-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export default function ClosuresSettings() {
  const { T, setSaved, setSettings, settings } = useSettings();
  const [showPast, setShowPast] = useState(false);
  const today = todayLocal();
  const list = Array.isArray(settings.closures) ? settings.closures : [];
  const write = (next) => { setSettings({ ...settings, closures: next }); setSaved(false); };
  const change = (id, patch) => write(list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const remove = (id) => write(list.filter((c) => c.id !== id));
  const add = (entry) => write([...list, { id: newId(), label: '', from: today, to: today, kind: 'closed', website: true, noticeDays: 7, message: '', ...entry }]);
  const holidays = upcomingHolidays(today).filter((h) => !list.some((c) => c.from <= h.date && (c.to || c.from) >= h.date));

  const ended = (c) => (c.to || c.from) < today;
  const shown = list.filter((c) => showPast || !ended(c)).sort((a, b) => (a.from || '').localeCompare(b.from || ''));
  const pastCount = list.filter(ended).length;

  const field = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '5px 8px', color: T.text, fontSize: 12.5, outline: 'none', fontFamily: 'inherit' };
  const small = { fontSize: 12, color: T.textSub };

  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Closures and special hours</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub }}>
        Holidays, a week off, winter hours, closed for the season. Enter them once: a closed day is never offered for online
        booking, fittings or rentals, texts that day get the after-hours reply, and your website shows a notice and the special
        hours. Press Save at the bottom when you are done.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 14px' }}>
        <button style={{ ...S.btn(true) }} onClick={() => add({})}>+ Add a closure</button>
        <select value="" onChange={(e) => { const h = holidays.find((x) => x.name === e.target.value); if (h) add({ label: h.name, from: h.date, to: h.date }); }} style={{ ...field, padding: '7px 8px' }}>
          <option value="">+ Add a holiday…</option>
          {holidays.map((h) => <option key={h.name} value={h.name}>{h.name} · {longDate(h.date, today)}</option>)}
        </select>
      </div>

      {!shown.length && <div style={{ ...small, marginBottom: 6 }}>No closures coming up.</div>}
      {shown.map((c) => {
        const past = ended(c);
        const now = !past && c.from <= today;
        return (
          <div key={c.id} style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 10, opacity: past ? 0.6 : 1 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={c.label || ''} placeholder="Name, e.g. Thanksgiving or Winter" maxLength={60} onChange={(e) => change(c.id, { label: e.target.value })} style={{ ...field, flex: '1 1 200px' }} />
              <select value={c.kind === 'hours' ? 'hours' : 'closed'} onChange={(e) => change(c.id, e.target.value === 'hours' ? { kind: 'hours', open: c.open || '10:00', close: c.close || '14:00' } : { kind: 'closed' })} style={field}>
                <option value="closed">Closed</option>
                <option value="hours">Open, special hours</option>
              </select>
              {c.kind === 'hours' && (
                <>
                  <input type="time" value={c.open || '10:00'} onChange={(e) => change(c.id, { open: e.target.value, close: c.close || '14:00', hours: '' })} style={field} />
                  <span style={small}>to</span>
                  <input type="time" value={c.close || '14:00'} onChange={(e) => change(c.id, { open: c.open || '10:00', close: e.target.value, hours: '' })} style={field} />
                </>
              )}
              {now && <span style={{ fontSize: 11, fontWeight: 600, color: '#2ecc71' }}>Now</span>}
              {past && <span style={{ fontSize: 11, color: T.textSub }}>Past</span>}
              <button onClick={() => remove(c.id)} style={{ ...S.btn(false), fontSize: 12, padding: '4px 10px', color: '#e74c3c', borderColor: T.border }}>Remove</button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
              <span style={small}>From</span>
              <input type="date" value={c.from || ''} onChange={(e) => change(c.id, { from: e.target.value, ...(c.to && c.to < e.target.value ? { to: e.target.value } : {}) })} style={field} />
              <span style={small}>to</span>
              <input type="date" value={c.to || c.from || ''} min={c.from || undefined} onChange={(e) => change(c.id, { to: e.target.value })} style={field} />
              <span style={small}>(the same day for one day)</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
              <label style={{ ...small, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={c.website !== false} onChange={(e) => change(c.id, { website: e.target.checked })} />
                Show on the website
              </label>
              {c.website !== false && (
                <>
                  <span style={small}>from</span>
                  <input type="number" min="0" max="60" value={c.noticeDays ?? 7} onChange={(e) => change(c.id, { noticeDays: e.target.value === '' ? 0 : Number(e.target.value) })} style={{ ...field, width: 56, textAlign: 'center' }} />
                  <span style={small}>days before</span>
                </>
              )}
            </div>
            {c.website !== false && c.from && (
              <div style={{ marginTop: 8 }}>
                <input value={c.message || ''} placeholder={preview({ ...c, message: '' }, today)} maxLength={200} onChange={(e) => change(c.id, { message: e.target.value })} style={{ ...field, width: '100%', boxSizing: 'border-box' }} />
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>The website's notice. Leave it empty for the wording shown, or write your own.</div>
              </div>
            )}
          </div>
        );
      })}
      {pastCount > 0 && (
        <button onClick={() => setShowPast((v) => !v)} style={{ background: 'none', border: 'none', padding: 0, color: T.textSub, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
          {showPast ? 'Hide past closures' : `Show past closures (${pastCount})`}
        </button>
      )}
    </div>
  );
}
